import type {
  AppLayoutDockItem,
  AppLayoutLayout,
} from '@evisort/eds/components/AppLayout/types';

import { AppLayout } from '@evisort/eds/components/AppLayout/AppLayout';
import {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { NAV_AND_SUBNAV_HEIGHT, NAV_HEIGHT } from '~/common/constants/page';
import { useDebounce } from '~/eds/src/hooks/useDebounce';
import { APP_BREADCRUMB_HEIGHT } from '~/features/app/AppBreadcrumb';
import { useSidebarNav } from '~/features/app/SidebarNavContext';
import { isBoundedLayout, RouteLayout } from '~/features/app/useRouteLayout';

import { useActiveToolbarPanelParam } from './activeToolbarPanelParam';
import './AppShell.css';
import { AppShellContext } from './AppShellContext';
import { useAppShellSlotRegistry } from './AppShellSlotRegistry';

const messages = defineMessages({
  panelActions: {
    id: 'AppShell.panelActions',
    defaultMessage: 'Panel actions',
    description:
      'Accessible label for the mobile bottom navigation of docked panel actions',
  },
  dismissPanel: {
    id: 'BottomSheet.dismiss',
    defaultMessage: 'Dismiss',
    description: 'Accessible label for the bottom sheet drag handle / close',
  },
});

interface AppShellProps extends PropsWithChildren {
  layout: RouteLayout;
  hasSubNav: boolean;
  banner?: ReactNode;
  breadcrumbs?: ReactNode;
  framed?: boolean;
  mainId?: string;
  sideNav?: ReactNode;
  topNav?: ReactNode;
}

const PANEL_WIDTH_CHANGE_DEBOUNCE_MS = 400;

const TOOLBAR_RAIL_ATTRIBUTES = { 'data-amp-track-region': 'toolbar' };

const TOOLBAR_PANEL_ATTRIBUTES = { 'data-testid': 'app-shell-panel' };

function resolveAppLayoutLayout(layout: RouteLayout): AppLayoutLayout {
  return layout === 'chromeless' ? 'page' : layout;
}

function SlotMount({ node, region }: { node: HTMLElement; region?: string }) {
  const hostRef = useCallback(
    (host: HTMLDivElement | null) => {
      if (host && host.firstChild !== node) {
        host.replaceChildren(node);
      }
    },
    [node],
  );

  return <div ref={hostRef} data-amp-track-region={region} />;
}

export function AppShell({
  layout,
  hasSubNav,
  banner,
  breadcrumbs,
  framed = true,
  mainId,
  sideNav,
  topNav,
  children,
}: AppShellProps) {
  const intl = useIntl();
  const {
    isCollapsed: isSidebarCollapsed,
    isMobile,
    isSidebarLayoutActive: hasNewSidebarNav,
    toggleCollapsed: toggleSidebar,
  } = useSidebarNav();
  const isSideNavOpen = isMobile && !isSidebarCollapsed;
  const closeSideNav = useCallback(() => {
    if (isSideNavOpen) toggleSidebar();
  }, [isSideNavOpen, toggleSidebar]);
  const { header, secondaryHeader, panels, rail } = useAppShellSlotRegistry();

  let topBarHeight = NAV_HEIGHT;
  if (hasNewSidebarNav) {
    topBarHeight = APP_BREADCRUMB_HEIGHT;
  } else if (hasSubNav) {
    topBarHeight = NAV_AND_SUBNAV_HEIGHT;
  }

  const {
    activePanelId: panelParam,
    openPanel,
    closePanel,
  } = useActiveToolbarPanelParam();

  const hasToolbar = panels.length > 0;

  const [isDefaultPanelRetired, setIsDefaultPanelRetired] = useState(false);
  const closeActivePanel = useCallback(() => {
    setIsDefaultPanelRetired(true);
    closePanel();
  }, [closePanel]);

  const panelsByKey = useMemo(
    () => new Map(panels.map((panel) => [panel.panelKey, panel])),
    [panels],
  );

  const defaultPanelKey =
    rail.defaultPanelKey && panelsByKey.has(rail.defaultPanelKey)
      ? rail.defaultPanelKey
      : null;
  const hasRegisteredPanels = panelsByKey.size > 0;

  const resolvedPanelKey =
    panelParam ?? (isDefaultPanelRetired ? null : defaultPanelKey);

  const dismissedPanelKeysRef = useRef<Set<string>>(new Set());
  if (
    resolvedPanelKey &&
    hasRegisteredPanels &&
    !panelsByKey.has(resolvedPanelKey)
  ) {
    dismissedPanelKeysRef.current.add(resolvedPanelKey);
  }

  const activePanelId =
    resolvedPanelKey &&
    panelsByKey.has(resolvedPanelKey) &&
    !dismissedPanelKeysRef.current.has(resolvedPanelKey)
      ? resolvedPanelKey
      : null;

  useEffect(() => {
    if (
      resolvedPanelKey &&
      hasRegisteredPanels &&
      !panelsByKey.has(resolvedPanelKey)
    ) {
      closeActivePanel();
    }
  }, [resolvedPanelKey, hasRegisteredPanels, panelsByKey, closeActivePanel]);

  const handleOpenPanel = useCallback(
    (key: string) => {
      dismissedPanelKeysRef.current.delete(key);
      openPanel(key);
    },
    [openPanel],
  );

  const openedPanelKeysRef = useRef<Set<string>>(new Set());
  if (rail.keepPanelsMounted && activePanelId) {
    openedPanelKeysRef.current.add(activePanelId);
  }

  const mountedPanelKeys = useMemo<ReadonlySet<string>>(() => {
    if (!rail.keepPanelsMounted) {
      return new Set(activePanelId ? [activePanelId] : []);
    }
    return new Set(
      panels
        .map((panel) => panel.panelKey)
        .filter((key) => openedPanelKeysRef.current.has(key)),
    );
  }, [rail.keepPanelsMounted, activePanelId, panels]);

  const widthMemoryKey = rail.keepPanelsMounted
    ? '__shared__'
    : (activePanelId ?? '');

  const [panelWidths, setPanelWidths] = useState<Record<string, number>>({});

  const activePanel = activePanelId
    ? panelsByKey.get(activePanelId)
    : undefined;

  const { onPanelWidthChange, forcedPanelWidth } = rail;
  const rememberedPanelWidth =
    forcedPanelWidth ?? panelWidths[widthMemoryKey] ?? rail.initialPanelWidth;
  const endContentWidth =
    rememberedPanelWidth ??
    (activePanel?.width === 'fill' ? 'auto' : undefined);
  const debouncedOnPanelWidthChange = useDebounce(
    (width: number) => onPanelWidthChange?.(width),
    PANEL_WIDTH_CHANGE_DEBOUNCE_MS,
    [onPanelWidthChange],
  );

  const handlePanelResize = useCallback(
    (width: number) => {
      if (forcedPanelWidth != null) {
        return;
      }
      setPanelWidths((current) => ({ ...current, [widthMemoryKey]: width }));
      debouncedOnPanelWidthChange(width);
    },
    [widthMemoryKey, debouncedOnPanelWidthChange, forcedPanelWidth],
  );

  const selectToolbarPanel = useCallback(
    (key: string) => {
      const panel = panelsByKey.get(key);
      if (panel?.disabled) {
        return;
      }
      if (panel?.actionOnly) {
        panel.onOpen?.();
        return;
      }
      if (key === activePanelId) {
        panel?.onOpen?.();
        closeActivePanel();
        return;
      }
      panel?.onOpen?.();
      handleOpenPanel(key);
    },
    [panelsByKey, activePanelId, handleOpenPanel, closeActivePanel],
  );

  const dismissActivePanel = useCallback(() => {
    if (!activePanelId || panelsByKey.get(activePanelId)?.disabled) {
      closeActivePanel();
      return;
    }
    selectToolbarPanel(activePanelId);
  }, [activePanelId, panelsByKey, closeActivePanel, selectToolbarPanel]);

  const [headerHeight, setHeaderHeight] = useState(0);
  const floatingBoundsRef = useRef<HTMLElement | null>(null);

  const growWithContent = !isBoundedLayout(layout);

  useEffect(() => {
    if (!growWithContent) {
      return;
    }
    const root = document.documentElement;
    root.style.scrollPaddingTop = `${topBarHeight + headerHeight}px`;
    return () => {
      root.style.scrollPaddingTop = '';
    };
  }, [growWithContent, topBarHeight, headerHeight]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      '--app-shell-sticky-offset',
      `${topBarHeight + headerHeight}px`,
    );
    return () => {
      root.style.removeProperty('--app-shell-sticky-offset');
    };
  }, [topBarHeight, headerHeight]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const rail = floatingBoundsRef.current?.querySelector(
      '[data-amp-track-region="toolbar"]',
    );
    function publishDockInsets() {
      const bounds = rail?.getBoundingClientRect();
      if (!bounds) {
        root.style.removeProperty('--app-shell-rail-inset');
        root.style.removeProperty('--app-shell-rail-top');
        root.style.removeProperty('--app-shell-bottom-inset');
        return;
      }
      root.style.setProperty(
        '--app-shell-rail-inset',
        `${root.clientWidth - bounds.left}px`,
      );
      root.style.setProperty('--app-shell-rail-top', `${bounds.top}px`);
      root.style.setProperty(
        '--app-shell-bottom-inset',
        `${window.innerHeight - bounds.bottom}px`,
      );
    }
    publishDockInsets();
    window.addEventListener('resize', publishDockInsets);
    return () => {
      window.removeEventListener('resize', publishDockInsets);
      root.style.removeProperty('--app-shell-rail-inset');
      root.style.removeProperty('--app-shell-rail-top');
      root.style.removeProperty('--app-shell-bottom-inset');
    };
  }, [hasToolbar, topBarHeight, headerHeight, layout]);

  const appShellContext = useMemo(
    () => ({
      layout,
      activePanelId,
      openPanel: handleOpenPanel,
      closePanel: closeActivePanel,
      mountedPanelKeys,
      stickyHeaderOffset: topBarHeight + headerHeight,
      floatingBoundsRef,
    }),
    [
      activePanelId,
      layout,
      handleOpenPanel,
      closeActivePanel,
      mountedPanelKeys,
      topBarHeight,
      headerHeight,
    ],
  );

  const dockItems = useMemo<AppLayoutDockItem[]>(
    () =>
      panels.map((panel) => ({
        content: mountedPanelKeys.has(panel.panelKey) ? (
          <SlotMount node={panel.container} region="sidepanel" />
        ) : undefined,
        count: panel.count,
        disabled: panel.disabled,
        icon: panel.icon,
        key: panel.panelKey,
        label: panel.tooltip || panel.label,
        maxCount: panel.maxCount,
        tooltip: panel.tooltip || undefined,
      })),
    [panels, mountedPanelKeys],
  );

  const dock = useMemo(
    () =>
      hasToolbar
        ? {
            activeKey: activePanelId,
            controlsLabel: intl.formatMessage(messages.panelActions),
            disabled: rail.disabled,
            dismissLabel: intl.formatMessage(messages.dismissPanel),
            items: dockItems,
            panelDataAttributes: TOOLBAR_PANEL_ATTRIBUTES,
            railDataAttributes: TOOLBAR_RAIL_ATTRIBUTES,
            resetKey: forcedPanelWidth ?? 'dock',
            width: endContentWidth,
            onClose: dismissActivePanel,
            onResize: handlePanelResize,
            onSelect: selectToolbarPanel,
          }
        : undefined,
    [
      activePanelId,
      dismissActivePanel,
      dockItems,
      endContentWidth,
      forcedPanelWidth,
      handlePanelResize,
      hasToolbar,
      intl,
      rail.disabled,
      selectToolbarPanel,
    ],
  );

  const headerSlot = useMemo(
    () => (header ? <SlotMount node={header} region="header" /> : undefined),
    [header],
  );
  const secondaryHeaderSlot = useMemo(() => {
    if (!banner && !secondaryHeader) {
      return undefined;
    }
    return (
      <>
        {banner}
        {secondaryHeader && <SlotMount node={secondaryHeader} />}
      </>
    );
  }, [banner, secondaryHeader]);
  const content = useMemo(
    () => (
      <div data-amp-track-region="main-content" style={{ display: 'contents' }}>
        {children}
      </div>
    ),
    [children],
  );

  return (
    <AppShellContext.Provider value={appShellContext}>
      <AppLayout
        breadcrumbs={breadcrumbs}
        dock={dock}
        framed={framed}
        header={headerSlot}
        layout={resolveAppLayoutLayout(layout)}
        mainId={mainId}
        rowRef={floatingBoundsRef}
        secondaryHeader={secondaryHeaderSlot}
        sideNav={sideNav}
        sideNavOpen={isSideNavOpen}
        topNav={topNav}
        onHeaderHeightChange={setHeaderHeight}
        onSideNavClose={closeSideNav}
      >
        {content}
      </AppLayout>
    </AppShellContext.Provider>
  );
}
